/**
 * Vertical panel resize with min/max constraints and localStorage persistence.
 * Drag upward increases height; downward decreases.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const STORAGE_PREFIX = "sellyx.ui.panelHeight.";

function readStoredHeight(storageKey, fallback) {
  if (!storageKey || typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${storageKey}`);
    if (raw == null) return fallback;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  } catch {
    // ignore
  }
  return fallback;
}

function writeStoredHeight(storageKey, height) {
  if (!storageKey || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${storageKey}`, String(Math.round(height)));
  } catch {
    // ignore
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * @param {{
 *   storageKey?: string,
 *   defaultHeight?: number,
 *   minHeight?: number,
 *   maxRatio?: number,
 *   enabled?: boolean,
 *   getMaxHeight?: () => number,
 * }} options
 */
export function useResizablePanel({
  storageKey = "",
  defaultHeight = 160,
  minHeight = 120,
  maxRatio = 0.5,
  enabled = true,
  getMaxHeight,
} = {}) {
  const [height, setHeight] = useState(() => readStoredHeight(storageKey, defaultHeight));
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef({ startY: 0, startHeight: 0 });

  useEffect(() => {
    setHeight(readStoredHeight(storageKey, defaultHeight));
  }, [storageKey, defaultHeight]);

  const resolveMax = useCallback(() => {
    if (typeof getMaxHeight === "function") {
      const max = getMaxHeight();
      if (Number.isFinite(max) && max > 0) return max;
    }
    if (typeof window !== "undefined") {
      return Math.floor(window.innerHeight * maxRatio);
    }
    return defaultHeight * 2;
  }, [getMaxHeight, maxRatio, defaultHeight]);

  const applyHeight = useCallback((next) => {
    const clamped = clamp(next, minHeight, resolveMax());
    setHeight(clamped);
    writeStoredHeight(storageKey, clamped);
    return clamped;
  }, [minHeight, resolveMax, storageKey]);

  // Keep height within max when viewport/container shrinks.
  useEffect(() => {
    if (!enabled) return undefined;
    const onResize = () => {
      setHeight((prev) => {
        const max = resolveMax();
        const next = clamp(prev, minHeight, max);
        if (next !== prev) writeStoredHeight(storageKey, next);
        return next;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [enabled, minHeight, resolveMax, storageKey]);

  const onResizePointerDown = useCallback((event) => {
    if (!enabled) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerId = event.pointerId;
    const target = event.currentTarget;
    target.setPointerCapture?.(pointerId);

    dragRef.current = { startY: event.clientY, startHeight: height };
    setDragging(true);

    const onMove = (e) => {
      const delta = dragRef.current.startY - e.clientY;
      applyHeight(dragRef.current.startHeight + delta);
    };

    const onUp = () => {
      setDragging(false);
      target.releasePointerCapture?.(pointerId);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, [enabled, height, applyHeight]);

  return {
    height,
    setHeight: applyHeight,
    dragging,
    onResizePointerDown,
    minHeight,
    maxRatio,
  };
}
